import { InputOtpRawPreset } from '@wangs-ui/core/dist/types/components/inputotp/inputotp.type';
import { TabMenuRawPreset } from '@wangs-ui/core/dist/types/components/tabmenu/tabmenu.type';
import { useRouterNavigationBridge } from '@wangs-ui/vue';
import PrimeVueConfig from 'primevue/config';
import { App, inject, Plugin } from 'vue';

import { AnimationDefaultConfig } from 'lib/components/animation/Animation.vue.d';
import { BadgeComponentConfigs } from 'lib/components/badge/Badge.vue.d';
import {
  ButtonBulkActionLocaleConfig,
  ButtonBulkActionProps,
} from 'lib/components/buttonbulkaction/ButtonBulkAction.vue.d';
import {
  ButtonDownloadLocaleConfig,
  ButtonDownloadProps,
} from 'lib/components/buttondownload/ButtonDownload.vue.d';
import { ButtonSearchLocaleConfig } from 'lib/components/buttonsearch/ButtonSearch.vue.d';
import {
  CalendarLocaleConfig,
  CalendarPresetOptions,
} from 'lib/components/calendar/Calendar.vue.d';
import { CustomColumnLocaleConfig } from 'lib/components/customcolumn/CustomColumn.vue.d';
import {
  DataTableLocaleConfig,
  DataTableProps,
  TreeTableProps,
} from 'lib/components/datatable/DataTable.vue.d';
import { DialogConfirmLocaleConfig } from 'lib/components/dialogconfirm/DialogConfirm.vue.d';
import { DialogFormLocaleConfig } from 'lib/components/dialogform/DialogForm.vue.d';
import {
  DropdownLocaleConfig,
  DropdownProps,
} from 'lib/components/dropdown/Dropdown.vue.d';
import {
  FieldWrapperLocaleConfig,
  FieldWrapperProps,
} from 'lib/components/fieldwrapper/FieldWrapper.vue.d';
import { FilterContainerLocaleConfig } from 'lib/components/filtercontainer/FilterContainer.vue.d';
import { ImageProps } from 'lib/components/image/Image.vue.d';
import { ImageCompressorLocaleConfig } from 'lib/components/imagecompressor/ImageCompressor.vue.d';
import { InputCurrencyLocaleConfig } from 'lib/components/inputcurrency/InputCurrency.vue.d';
import { InputEmailProps } from 'lib/components/inputemail/InputEmail.vue.d';
import { InputNumberLocaleConfig } from 'lib/components/inputnumber/InputNumber.vue.d';
import { InputPhoneNumberLocaleConfig } from 'lib/components/inputphonenumber/InputPhoneNumber.vue.d';
import { InputrangeNumberLocaleConfig } from 'lib/components/inputrangenumber/InputRangeNumber.vue.d';
import {
  InputTextLocaleConfig,
  InputTextProps,
} from 'lib/components/inputtext/InputText.vue.d';
import { InputURLProps } from 'lib/components/inputurl/InputURL.vue.d';
import { LoadingPresetOptions } from 'lib/components/loading/Loading.vue.d';
import { MenuLocaleConfig } from 'lib/components/menu/Menu.vue.d';
import { MultiSelectLocaleConfig } from 'lib/components/multiselect/MultiSelect.vue.d';
import { OverlayPanelPassThroughOptions } from 'lib/components/overlaypanel/OverlayPanel.vue.d';
import { TagTypeProps } from 'lib/components/tagtype/TagType.vue.d';
import { TextareaLocaleConfig } from 'lib/components/textarea/Textarea.vue.d';
import { ToastLocaleConfig } from 'lib/components/toast/Toast.vue.d';
import { TreeProps } from 'lib/components/tree/Tree.vue.d';
import {
  UserNameComponentConfigs,
  UserNameLocaleConfig,
  UserNamePresetOptions,
} from 'lib/components/username/UserName.vue.d';
import { type UseToastConfig } from 'lib/utils/baseToast.util';
import { deepMerge } from 'lib/utils/object.util';

/**
 * A Record of component names with theirs props interface
 */
export interface ComponentDefaultPropsConfig {
  FieldWrapper?: FieldWrapperProps;
  InputText?: InputTextProps;
  InputURL?: InputURLProps;
  InputEmail?: InputEmailProps;
  Tree?: TreeProps;
  Image?: ImageProps;
  ButtonBulkAction?: Omit<ButtonBulkActionProps, 'selectedData' | 'options'>;
  Animation?: AnimationDefaultConfig;
  DataTable?: Partial<DataTableProps | TreeTableProps>;
  Badge?: Partial<BadgeComponentConfigs>;
  UserName?: UserNameComponentConfigs;
  Dropdown?: DropdownProps;
  TagType?: Partial<TagTypeProps>;
  ButtonDownload?: Partial<ButtonDownloadProps>;
}

interface ComponentLocaleConfig {
  FieldWrapper?: FieldWrapperLocaleConfig;
  InputText?: InputTextLocaleConfig;
  DialogConfirm?: DialogConfirmLocaleConfig;
  CustomColumn?: CustomColumnLocaleConfig;
  ButtonSearch?: ButtonSearchLocaleConfig;
  FilterContainer?: FilterContainerLocaleConfig;
  MultiSelect?: MultiSelectLocaleConfig;
  Dropdown?: DropdownLocaleConfig;
  InputCurrency?: InputCurrencyLocaleConfig;
  TextArea?: TextareaLocaleConfig;
  InputRangeNumber?: InputrangeNumberLocaleConfig;
  DialogForm?: DialogFormLocaleConfig;
  InputPhoneNumber?: InputPhoneNumberLocaleConfig;
  InputNumber?: InputNumberLocaleConfig;
  Calendar?: CalendarLocaleConfig;
  Menu?: MenuLocaleConfig;
  ButtonBulkAction?: ButtonBulkActionLocaleConfig;
  UserName?: UserNameLocaleConfig;
  DataTable?: DataTableLocaleConfig;
  ImageCompressor?: ImageCompressorLocaleConfig;
  ButtonDownload?: ButtonDownloadLocaleConfig;
  Toast?: ToastLocaleConfig;
}

export interface LocaleConfig {
  global: Partial<typeof DEFAULT_GLOBAL_LOCALE_CONFIG>;
  components: ComponentLocaleConfig;
}

export interface ComponentPresetOptions {
  calendar: CalendarPresetOptions;
  overlaypanel: OverlayPanelPassThroughOptions;
  loading: LoadingPresetOptions;
  username: UserNamePresetOptions;
  inputotp: InputOtpRawPreset;
  tabmenu: TabMenuRawPreset;
  [key: string]: any;
}

export type WangsVueComponentConfig<
  ComponentName extends
    | keyof ComponentDefaultPropsConfig
    | keyof ComponentLocaleConfig,
> = {
  defaultProps: ComponentName extends keyof ComponentDefaultPropsConfig
    ? ComponentDefaultPropsConfig[ComponentName]
    : undefined;
  locale: (ComponentName extends keyof ComponentLocaleConfig
    ? ComponentLocaleConfig[ComponentName]
    : undefined) & { global: LocaleConfig['global'] };
};

export interface WangsVueOptions {
  defaultProps: ComponentDefaultPropsConfig;
  locale: LocaleConfig;
  useToastConfig?: UseToastConfig;
  zIndex?: Record<string, number>;
  filterMatchModeOptions?: Record<string, string[]>;
  /**
   * @deprecated - partially replaced with {@link preset}
   */
  pt?: ComponentPresetOptions;
  preset?: ComponentPresetOptions;
  ptOptions?: {
    mergeSections: boolean;
    mergeProps: boolean;
  };
  /**
   * @deprecated - Will always be unstyled
   */
  unstyled?: boolean;
  csp?: {
    nonce?: string;
  };
}

export interface WangsVueInstance {
  config: WangsVueOptions;

  getComponentConfig<
    ComponentName extends
      | keyof ComponentDefaultPropsConfig
      | keyof ComponentLocaleConfig,
  >(
    componentName: ComponentName,
  ): WangsVueComponentConfig<ComponentName>;
}

export const DEFAULT_GLOBAL_LOCALE_CONFIG = {
  startsWith: 'Starts with',
  contains: 'Contains',
  notContains: 'Not contains',
  endsWith: 'Ends with',
  equals: 'Equals',
  notEquals: 'Not equals',
  noFilter: 'No Filter',
  lt: 'Less than',
  lte: 'Less than or equal to',
  gt: 'Greater than',
  gte: 'Greater than or equal to',
  dateIs: 'Date is',
  dateIsNot: 'Date is not',
  dateBefore: 'Date is before',
  dateAfter: 'Date is after',
  clear: 'Clear',
  apply: 'Apply',
  matchAll: 'Match All',
  matchAny: 'Match Any',
  addRule: 'Add Rule',
  removeRule: 'Remove Rule',
  accept: 'Yes',
  reject: 'No',
  choose: 'Choose',
  upload: 'Upload',
  cancel: 'Cancel',
  completed: 'Completed',
  pending: 'Pending',
  fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
  monthNames: [
    'Januari',
    'Februari',
    'Maret',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Agustus',
    'September',
    'Oktober',
    'November',
    'Desember',
  ],
  monthNamesShort: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'Mei',
    'Jun',
    'Jul',
    'Agu',
    'Sep',
    'Okt',
    'Nov',
    'Des',
  ],
  dayNames: ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'],
  dayNamesShort: ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'],
  dayNamesMin: ['Mi', 'Se', 'Se', 'Ra', 'Ka', 'Ju', 'Sa'],
  chooseYear: 'Choose Year',
  chooseMonth: 'Choose Month',
  chooseDate: 'Choose Date',
  prevDecade: 'Previous Decade',
  nextDecade: 'Next Decade',
  prevYear: 'Previous Year',
  nextYear: 'Next Year',
  prevMonth: 'Previous Month',
  nextMonth: 'Next Month',
  prevHour: 'Previous Hour',
  nextHour: 'Next Hour',
  prevMinute: 'Previous Minute',
  nextMinute: 'Next Minute',
  prevSecond: 'Previous Second',
  nextSecond: 'Next Second',
  pm: 'PM',
  am: 'AM',
  today: 'Today',
  weekHeader: 'Wk',
  firstDayOfWeek: 0,
  showMonthAfterYear: false,
  dateFormat: 'mm/dd/yy',
  weak: 'Weak',
  medium: 'Medium',
  strong: 'Strong',
  passwordPrompt: 'Enter a password',
  emptyFilterMessage: 'No results found', // @deprecated Use 'emptySearchMessage' option instead.
  searchMessage: '{0} results are available',
  selectionMessage: '{0} items selected',
  emptySelectionMessage: 'No selected item',
  emptySearchMessage: 'No results found',
  emptyMessage: 'No available options',
  aria: {
    trueLabel: 'True',
    falseLabel: 'False',
    nullLabel: 'Not Selected',
    star: '1 star',
    stars: '{star} stars',
    selectAll: 'All items selected',
    unselectAll: 'All items unselected',
    close: 'Close',
    previous: 'Previous',
    next: 'Next',
    navigation: 'Navigation',
    scrollTop: 'Scroll Top',
    moveTop: 'Move Top',
    moveUp: 'Move Up',
    moveDown: 'Move Down',
    moveBottom: 'Move Bottom',
    moveToTarget: 'Move to Target',
    moveToSource: 'Move to Source',
    moveAllToTarget: 'Move All to Target',
    moveAllToSource: 'Move All to Source',
    pageLabel: 'Page {page}',
    firstPageLabel: 'First Page',
    lastPageLabel: 'Last Page',
    nextPageLabel: 'Next Page',
    prevPageLabel: 'Previous Page',
    rowsPerPageLabel: 'Rows per page',
    jumpToPageDropdownLabel: 'Jump to Page Dropdown',
    jumpToPageInputLabel: 'Jump to Page Input',
    selectRow: 'Row Selected',
    unselectRow: 'Row Unselected',
    expandRow: 'Row Expanded',
    collapseRow: 'Row Collapsed',
    showFilterMenu: 'Show Filter Menu',
    hideFilterMenu: 'Hide Filter Menu',
    filterOperator: 'Filter Operator',
    filterConstraint: 'Filter Constraint',
    editRow: 'Row Edit',
    saveEdit: 'Save Edit',
    cancelEdit: 'Cancel Edit',
    listView: 'List View',
    gridView: 'Grid View',
    slide: 'Slide',
    slideNumber: '{slideNumber}',
    zoomImage: 'Zoom Image',
    zoomIn: 'Zoom In',
    zoomOut: 'Zoom Out',
    rotateRight: 'Rotate Right',
    rotateLeft: 'Rotate Left',
    listLabel: 'Option List',
  },
};

export const defaultOptions: WangsVueOptions = {
  locale: {
    global: DEFAULT_GLOBAL_LOCALE_CONFIG,
    components: {
      FieldWrapper: {
        labelOptional: '(opsional)',
        labelRequired: '*',
      },
      InputText: {
        urlFormatInvalidText: 'Format URL tidak valid',
        emailFormatInvalidText: 'Format email tidak valid',
        defaultPlaceholder: 'Tulis {label}',
        alreadyExistInvalidText: '{label} sudah ada',
        emptyInvalidText: '{label} harus diisi',
        exceedMaxLengthInvalidText: 'Maks. {maxLength} karakter',
        includeSpecialCharsInvalidText: 'Tidak boleh berisi spesial karakter',
      },
      DialogConfirm: {
        nonActionableCloseButtonLabel: 'Tutup',
        actionableCloseButtonLabel: 'Batal',
        defaultConfirmMessage: 'Apa kamu yakin?',
        defaultConfirmLabel: 'Yakin',
      },
      CustomColumn: {
        resetDefaultText: 'Atur Ulang ke Default',
      },
      ButtonSearch: {
        searchPlaceholder: 'Cari',
      },
      FilterContainer: {
        applyText: 'Terapkan',
        clearFieldText: 'Bersihkan Field',
      },
      MultiSelect: {
        filterPlaceholder: 'Cari',
        loadingPlaceholder: 'Memuat...',
        inputPlaceholder: 'Pilih {lowercaseLabel}',
        emptySelectionErrorMessage: '{label} harus dipilih',
      },
      Dropdown: {
        filterPlaceholder: 'Cari',
        loadingPlaceholder: 'Memuat...',
        inputPlaceholder: 'Pilih {lowercaseLabel}',
        emptySelectionErrorMessage: '{label} harus dipilih',
      },

      InputCurrency: {
        emptyInputErrorMessage: '{label} harus diisi',
      },

      TextArea: {
        defaultPlaceholder: 'Tulis {label}',
        emptyInvalidText: '{label} harus diisi',
        exceedMaxLengthInvalidText: 'Maks. {maxLength} karakter',
      },

      InputRangeNumber: {
        minPlaceholder: 'Tulis',
        maxPlaceholder: 'Tulis',
      },

      DialogForm: {
        cancelBtnLabel: 'Batal',
        clearBtnLabel: 'Bersihkan Field',
        stayCheckboxLabel: 'Tetap di halaman ini',
        submitBtnLabel: 'Simpan',
        asideRightCollapsedButtonLabel: 'Lihat data yang ada >',
        asideRightExpandedButtonLabel: 'Tutup <',
      },

      InputPhoneNumber: {
        defaultPlaceholder: 'Masukkan nomor telepon',
        emptyInvalidText: '{label} harus diisi',
      },

      InputNumber: {
        defaultPlaceholder: 'Tulis {label}',
        alreadyExistInvalidText: '{label} sudah ada',
        emptyInvalidText: '{label} harus diisi',
      },

      Calendar: {
        defaultPlaceholder: 'Pilih tanggal',
        emptyInvalidText: 'Tanggal tidak boleh kosong',
        cancelFooterButtonLabel: 'Batal',
        applyFooterButtonLabel: 'Terapkan',
      },

      Menu: {
        noOptionLabel: 'Tidak ada aksi',
      },

      ButtonBulkAction: {
        allRecordSelected: 'All {naming} Selected',
        selectAllRecords: 'Select All ({totalRecords} data)',
        currentSelectionMessage: '{length} {naming} Selected',
      },

      UserName: {
        detailUserAnchorText: 'User Detail',
      },

      DataTable: {
        downloadingMessage: 'Downloading...',
        falseText: 'No',
        trueText: 'Yes',
        paginationReportFound: 'Showing {first} to {last} of {totalRecords}',
        paginationReportNotFound: 'No Data Found',
      },

      ButtonDownload: {
        downloadingMessage: 'Downloading...',
        falseText: 'No',
        trueText: 'Yes',
      },

      ImageCompressor: {
        cropperCtrlScrollInfo: 'Use Ctrl + Scroll to zoom the cropper',
        cropperTwoFingerInfo: 'Use two fingers to move the cropper',
        cropDialogHeader: 'Adjust Photo',
        imageTypeRequirement: 'Must be image format',
        maxSizeRequirement: 'Max. 1 MB',
        cancelCropLabel: 'Cancel',
        applyCropLabel: 'Apply',
        changeImageLabel: 'Change Image',
      },
    },
  },
  defaultProps: {
    FieldWrapper: {
      showOptionalText: true,
      showAsterisk: false,
      tooltipPos: 'top',
    },

    Tree: {
      type: 'category',
      filter: '',
    },

    InputText: {
      maxLength: 30,
    },

    InputURL: {
      maxLength: undefined, // Unlimited
    },

    InputEmail: {
      maxLength: 60,
    },

    ButtonBulkAction: {
      selectMenuType: 'overlay-panel',
      naming: 'Data',
    },

    Animation: {
      libPath: 'workspace',
    },

    TagType: {
      useNfc: true,
    },

    UserName: {
      showUserName: true,
      formatUserName: true,
    },
  },
};

const WangsVueSymbol = Symbol('WangsVue');

export const useWangsVueConfig = <
  T extends
    | (keyof ComponentDefaultPropsConfig | keyof ComponentLocaleConfig)
    | undefined,
>(
  componentName?: T,
): T extends keyof ComponentDefaultPropsConfig | keyof ComponentLocaleConfig
  ? WangsVueComponentConfig<T>
  : WangsVueOptions => {
  const WangsVue = inject<WangsVueInstance>(WangsVueSymbol);

  if (!WangsVue) {
    throw new Error('WangsVue is not installed!');
  }

  if (componentName) {
    return WangsVue.getComponentConfig(componentName) as T extends
      | keyof ComponentDefaultPropsConfig
      | keyof ComponentLocaleConfig
      ? WangsVueComponentConfig<T>
      : never;
  }

  return WangsVue.config as T extends
    | keyof ComponentDefaultPropsConfig
    | keyof ComponentLocaleConfig
    ? never
    : WangsVueOptions;
};

export const mergeWangsVueConfig = (
  baseConfig: WangsVueOptions,
  overrideConfig: Partial<WangsVueOptions>,
): WangsVueOptions => {
  return deepMerge(baseConfig, overrideConfig);
};

/**
 *
 * @param predefinedOptions
 * @param mergeConfig Wether merge predefined config with defualt config @default true
 * @returns
 */
const createWangsVuePlugin = (
  predefinedOptions: Partial<WangsVueOptions> = defaultOptions,
  mergeConfig: boolean = true,
): Plugin<Partial<WangsVueOptions>> => {
  const mergedConfig = mergeConfig
    ? mergeWangsVueConfig(defaultOptions, predefinedOptions)
    : predefinedOptions;

  return {
    install: (app: App, options?: Partial<WangsVueOptions>): void => {
      const configOptions: WangsVueOptions = {
        ...mergedConfig,
        ...options,
        preset: options.preset ?? options.pt,

        locale: {
          ...mergedConfig.locale,
          ...options?.locale,
          components: {
            ...mergedConfig.locale.components,
            ...(options?.locale?.components || {}),
          },
        },

        defaultProps: {
          ...mergedConfig.defaultProps,
          ...(options?.defaultProps || {}),
        },

        useToastConfig: mergedConfig.useToastConfig,
      };

      const WangsVue: WangsVueInstance = {
        config: configOptions,

        getComponentConfig: (componentName: string) => ({
          defaultProps: configOptions.defaultProps[componentName] || {},
          locale: {
            ...(configOptions.locale.components[componentName] || {}),
            global: configOptions.locale.global,
          },
        }),
      };

      app.config.globalProperties.$wangsvue = WangsVue;
      app.config.globalProperties.$preset = WangsVue.config.preset;
      app.provide(WangsVueSymbol, WangsVue);
      app.provide('preset', configOptions.preset);
      app.provide('useToastConfig', configOptions.useToastConfig);
      app.use(PrimeVueConfig, {
        unstyled: true,
        pt: configOptions.preset,
        locale: configOptions.locale.global,
      });

      app.use(useRouterNavigationBridge);
    },
  };
};

export default createWangsVuePlugin;

declare module 'vue' {
  export interface ComponentCustomProperties {
    $wangsvue: WangsVueInstance;
    $preset: ComponentPresetOptions;
  }
}
